import { Button, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  invalidResult,
  maxCharactersValidator,
  requiredValidator,
  uniqueValidator,
} from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../api/adminApi";

interface Props {
  open: boolean;
  onClose: () => void;
  existingInvestorNames: string[];
  onCreated: (investorId: string, name: string) => void;
}

const createInvestor = withErrorHandling(adminApi.createInvestor);

const maxNameLength = 100;

const InvestorCreateDialog = ({ open, onClose, existingInvestorNames, onCreated }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [name, setName] = useState("");
  const [validationResult, setValidationResult] = useState<ValidationResult>(() => invalidResult(""));
  const [isSaving, setIsSaving] = useState(false);

  const validateName = combineValidators(
    requiredValidator,
    maxCharactersValidator(maxNameLength),
    uniqueValidator(
      "An investor with this name already exists",
      existingInvestorNames.map((name) => name.toLowerCase())
    )
  );

  const handleNameChange = (name: string) => {
    setName(name);
    setValidationResult(validateName(name.toLowerCase()));
  };

  const handleCreate = async () => {
    setIsSaving(true);
    const [investor, error] = await createInvestor({ name });
    setIsSaving(false);

    if (error) {
      logError(error, "[InvestorCreateDialog]");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Investor created successfully");
    onCreated(investor.id, name);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => setName(""));
    setTimeout(() => setValidationResult(invalidResult("")));
  };

  return (
    <SmallDialog open={open} onClose={handleClose}>
      <DialogTitle>Add Investor</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <DialogContent>
        <TextField
          fullWidth
          label="Name"
          variant="outlined"
          onChange={(e) => handleNameChange(e.target.value)}
          error={!!validationResult.error}
          helperText={validationResult.error}
          slotProps={{
            htmlInput: { maxLength: maxNameLength },
            input: {
              endAdornment: (
                <Typography variant="caption" color="textSecondary">
                  {maxNameLength - name.length}
                </Typography>
              ),
            },
          }}
        />
      </DialogContent>
      <DialogButtons onCancel={handleClose}>
        <Button loading={isSaving} variant="contained" disabled={!validationResult.isValid} onClick={handleCreate}>
          Create
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default InvestorCreateDialog;
